import React, { useEffect, useState } from 'react';
import {
  TableRow,
  TableCell,
  Paper,
  AvatarGroup,
  Avatar,
  Dialog,
  DialogContent,
  IconButton,
  Box,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import Table from '../../../components/Table';

import { TableResponse } from '../../../schema/helper';
import { api } from '../../../api';
import dayjs from 'dayjs';
import { CCTVFilter, getCCTVSearchParams } from './filter';

// OST valid on the scan date
interface ScanCCTVOST {
  is_active: boolean;
  course_name: string | null;
  training_date: string | null;
  expire_date: string | null;
}
// work permit covering the scan date
interface ScanCCTVWPM {
  is_active: boolean;
  // false = the camera gate area doesn't require WPM
  is_required: boolean | null;
  // work permit has the camera gate area, null = gate has no area (not checked)
  is_area_match: boolean | null;
  area_id: number | null;
  area_name: string | null;
  work_permit_id: string | null;
  work_permit_no: string | null;
  work_start_date: string | null;
  work_end_date: string | null;
  location: string | null;
  status: string | null;
}
// GET /scan-cctv row
interface ScanCCTV {
  id: number;
  personal_id: string | null;
  person_id: number | null;
  company_id: number | null;
  status_id: number | null;
  gate_id: number | null;
  person_type_id: number | null;

  name: string;
  person_type: string | null;
  company: string | null;
  status: string | null;
  gate: string | null;
  area: string | null;
  created_time: string;

  // photo captured by the camera (FaceMe webhook)
  photo: string | null;
  // registered photo of the matched person
  person_photo: string | null;

  is_unknown: boolean;
  external_person_id: string | null;
  device_name: string | null;

  ost: ScanCCTVOST | null;
  wpm: ScanCCTVWPM | null;
  // result at snap time, null for a scan saved before the check existed
  is_pass: boolean | null;
  remark: string | null;
}
type TableInOutEachGateProps = CCTVFilter;

const tableHead = [
  'ภาพบุคคล',
  'เลขประจำตัวประชาชน',
  'ชื่อ นามสกุล',
  'ประเภทบุคคล',
  'บริษัท',
  'เวลา',
  'ประตู',
  'OST',
  'Work Permit',
  'ผลการตรวจ',
  'สาเหตุ'
];
const TableInOutEachGate: React.FC<TableInOutEachGateProps> = ({
  filterID,
  filterName,
  filterGateIDList,
  filterCompanyIDList,
  filterPersonTypeIDList,
  filterStartDate,
  filterEndDate
}: TableInOutEachGateProps) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [data, setData] = useState<TableResponse<ScanCCTV> | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);
  const [selectedName, setSelectedName] = useState<string>('');

  const handleOpenPhoto = (photoUrl: string | null, name: string) => {
    if (!photoUrl) {
      return;
    }
    setSelectedPhoto(photoUrl);
    setSelectedName(name);
  };

  const handleClosePhoto = () => {
    setSelectedPhoto(null);
    setSelectedName('');
  };

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  useEffect(() => {
    setIsLoading(true);
    const timeOutId = setTimeout(() => {
      const searchParams = getCCTVSearchParams({
        filterID,
        filterName,
        filterGateIDList,
        filterCompanyIDList,
        filterPersonTypeIDList,
        filterStartDate,
        filterEndDate
      });
      searchParams.set('offset', `${page * rowsPerPage}`);
      searchParams.set('limit', `${rowsPerPage}`);
      api
        .get<TableResponse<ScanCCTV>>(`/scan-cctv`, { params: searchParams })
        .then((res) => {
          setData(res.data);
        })
        .finally(() => setIsLoading(false));
    }, 500);
    return () => clearTimeout(timeOutId);
  }, [
    filterID,
    filterName,
    filterGateIDList,
    filterCompanyIDList,
    filterPersonTypeIDList,
    filterStartDate,
    filterEndDate,
    page,
    rowsPerPage
  ]);

  return (
    <>
      <Paper>
        <Table
          heads={tableHead}
          count={data?.total ? data?.total : 0}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          isLoading={isLoading}
        >
          {data?.data.map((data, index) => (
            <TableRow key={index}>
              <TableCell align="center" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <AvatarGroup spacing={24}>
                  {/* photo captured by the camera, red ring = person not found */}
                  <Avatar
                    src={data.photo ?? undefined}
                    alt={`${data.name}`}
                    onClick={() => handleOpenPhoto(data.photo, data.name)}
                    sx={{
                      width: 56,
                      height: 56,
                      cursor: data.photo ? 'pointer' : 'default',
                      p: data.is_unknown ? '4px' : 0,
                      background: data.is_unknown ? '#dc2743' : 'transparent',
                      borderRadius: '50%',
                      '& .MuiAvatar-img': {
                        borderRadius: '50%',
                        boxSizing: 'border-box',
                        border: '2px solid #fff'
                      },
                      '&:hover': {
                        transform: 'scale(1.08)',
                        transition: 'transform 0.2s ease-in-out'
                      }
                    }}
                  />

                  {/* registered photo of the matched person */}
                  {!data.is_unknown && (
                    <Avatar
                      src={data.person_photo ?? undefined}
                      alt={`${data.name}`}
                      onClick={() => handleOpenPhoto(data.person_photo, data.name)}
                      sx={{
                        width: 56,
                        height: 56,
                        cursor: data.person_photo ? 'pointer' : 'default',
                        border: '2px solid #fff',
                        '&:hover': {
                          transform: 'scale(1.08)',
                          transition: 'transform 0.2s ease-in-out'
                        }
                      }}
                    />
                  )}
                </AvatarGroup>
              </TableCell>
              <TableCell align="center">{data.personal_id || <Typography color='error'>ไม่พบข้อมูล</Typography>}</TableCell>
              <TableCell align="center">{!data.is_unknown ? data.name : <Typography color='error'>ไม่พบข้อมูล</Typography>}</TableCell>
              <TableCell align="center">{data.person_type || <Typography color='error'>ไม่พบข้อมูล</Typography>}</TableCell>
              <TableCell align="center">{data.company || <Typography color='error'>ไม่พบข้อมูล</Typography>}</TableCell>
              <TableCell align="center">
                {dayjs(data.created_time).format('DD/MM/YYYY | HH:mm')}
              </TableCell>
              <TableCell align="center">{data.gate || '-'}</TableCell>
              <TableCell align="center">
                {data.ost ? (
                  data.ost.is_active ? (
                    <Typography color='success.main' variant='body2'>
                      หมดอายุ {data.ost.expire_date ? dayjs(data.ost.expire_date).format('DD/MM/YYYY') : '-'}
                    </Typography>
                  ) : (
                    <Typography color='error' variant='body2'>ไม่มี</Typography>
                  )
                ) : '-'}
              </TableCell>
              <TableCell align="center">
                {data.wpm ? (
                  data.wpm.is_required === false ? (
                    <Typography variant='body2'>ไม่ต้องใช้</Typography>
                  ) : !data.wpm.is_active ? (
                    <Typography color='error' variant='body2'>ไม่มี</Typography>
                  ) : data.wpm.is_area_match === false ? (
                    <Typography color='error' variant='body2'>
                      {data.wpm.work_permit_no || '-'} (ไม่ครอบคลุม {data.wpm.area_name})
                    </Typography>
                  ) : (
                    <Typography color='success.main' variant='body2'>{data.wpm.work_permit_no || '-'}</Typography>
                  )
                ) : '-'}
              </TableCell>
              <TableCell align="center">
                {data.is_pass === null ? '-' : data.is_pass ? <Typography color='success.main'>ผ่าน</Typography> : <Typography color='error'>ไม่ผ่าน</Typography>}
              </TableCell>
              <TableCell align="center">{data.remark ? <Typography color='error'>{data.remark}</Typography> : '-'}</TableCell>
            </TableRow>
          ))}
        </Table>
      </Paper>

      <Dialog
        open={Boolean(selectedPhoto)}
        onClose={handleClosePhoto}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            backgroundColor: '#f5f5f5'
          }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
          <IconButton aria-label="close photo" onClick={handleClosePhoto}>
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2, pt: 0 }}>
          <Box
            component="img"
            src={selectedPhoto ?? undefined}
            alt={selectedName || 'Person photo'}
            sx={{
              maxWidth: '100%',
              maxHeight: '75vh',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
              borderRadius: 2,
              boxShadow: 3,
              backgroundColor: '#fff'
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TableInOutEachGate;
